import {
  computeReadiness,
  type ChatMessage,
  type DomainKey,
  type InterviewEngine,
  type InterviewState,
  type QuestionCandidate,
  type ReadinessProfile,
  type Recommendation,
  type UnderstandingItem,
} from './model';

/**
 * Moteur d'entretien **de démonstration** : un scénario écrit à l'avance sur une entreprise fictive
 * (Maison Exemple SAS), pour concevoir et éprouver l'écran avant le vrai moteur. Il ne comprend pas
 * la réponse saisie : chaque tour applique les mises à jour prévues, et une réponse type est
 * proposée. Tout ce qu'il produit est marqué « simulé ». La readiness, elle, est réellement
 * calculée à partir des faits (model.ts). Aucune donnée réelle de client n'y figure.
 */

const PROFILE: ReadinessProfile = {
  company: [
    { item: 'legal_name', weight: 1 },
    { item: 'activity', weight: 1 },
  ],
  organization: [
    { item: 'decision_maker', weight: 1 },
    { item: 'roles', weight: 2, blocking: true },
    { item: 'headcount', weight: 1 },
    { item: 'sites', weight: 2, blocking: true },
    { item: 'user_count', weight: 1 },
  ],
  products: [{ item: 'product_families', weight: 2 }],
  sales: [
    { item: 'sales_channels', weight: 2 },
    { item: 'customer_types', weight: 1 },
    { item: 'order_entry', weight: 2 },
  ],
  inventory: [
    { item: 'stock_locations', weight: 2 },
    { item: 'stock_system', weight: 1 },
  ],
  production: [
    { item: 'production_recipes', weight: 2 },
    { item: 'production_orders', weight: 1 },
  ],
  quality: [
    { item: 'quality_owner', weight: 1 },
    { item: 'quality_controls', weight: 2 },
    { item: 'haccp_plan', weight: 2 },
  ],
  traceability: [
    { item: 'lot_tracking', weight: 3, blocking: true },
    { item: 'traceability_tool', weight: 1 },
  ],
  accounting: [
    { item: 'accounting_system', weight: 2 },
    { item: 'accounting_owner', weight: 1 },
  ],
  systems: [
    { item: 'current_systems', weight: 1 },
    { item: 'odoo_scope', weight: 2, blocking: true },
    { item: 'shopify', weight: 1 },
  ],
};

const SCOPE: DomainKey[] = ['company', 'organization', 'products', 'sales', 'inventory', 'production', 'quality', 'traceability', 'accounting', 'systems'];

function initialItems(): UnderstandingItem[] {
  const missing = (id: string, domain: DomainKey, label: string): UnderstandingItem => ({ id, domain, label });
  return [
    { id: 'legal_name', domain: 'company', label: 'Raison sociale', value: 'Maison Exemple SAS', epistemic_status: 'observed', source: 'official_registry', statement: 'Registre officiel (simulé)' },
    { id: 'activity', domain: 'company', label: 'Activité déclarée', value: 'Préparation de produits à base de viande (NAF 10.13A)', epistemic_status: 'observed', source: 'official_registry', statement: 'Registre officiel (simulé)' },
    { id: 'sites', domain: 'organization', label: 'Sites', value: '2 établissements', epistemic_status: 'observed', source: 'official_registry', statement: 'Registre officiel (simulé)' },
    missing('decision_maker', 'organization', 'Interlocuteur et décideur'),
    missing('roles', 'organization', 'Responsabilités'),
    missing('headcount', 'organization', 'Effectif'),
    { id: 'user_count', domain: 'organization', label: 'Utilisateurs prévus', value: '6 à 20', epistemic_status: 'declared', source: 'booking_form', statement: 'Formulaire de rendez-vous' },
    { id: 'product_families', domain: 'products', label: 'Familles de produits', value: 'Saucissons, terrines', epistemic_status: 'hypothetical', source: 'company_website', confidence: 0.7, statement: 'Pages « Nos produits » du site (simulé)' },
    { id: 'customer_types', domain: 'customers', label: 'Types de clients', value: 'Particuliers (boutique en ligne)', epistemic_status: 'hypothetical', source: 'company_website', confidence: 0.5, statement: 'Site web (simulé)' },
    { id: 'sales_channels', domain: 'sales', label: 'Canaux de vente', value: 'Boutique en ligne', epistemic_status: 'hypothetical', source: 'company_website', confidence: 0.6, statement: 'Site web (simulé)' },
    missing('order_entry', 'sales', 'Saisie des commandes'),
    missing('stock_locations', 'inventory', 'Lieux de stockage'),
    missing('stock_system', 'inventory', 'Outil de gestion des stocks'),
    missing('production_recipes', 'production', 'Recettes et nomenclatures'),
    missing('production_orders', 'production', 'Ordres de fabrication'),
    missing('quality_owner', 'quality', 'Responsable qualité'),
    missing('quality_controls', 'quality', 'Contrôles qualité'),
    missing('haccp_plan', 'quality', 'Plan HACCP'),
    missing('lot_tracking', 'traceability', 'Suivi des lots'),
    missing('traceability_tool', 'traceability', 'Outil de traçabilité'),
    missing('accounting_system', 'accounting', 'Outil comptable'),
    missing('accounting_owner', 'accounting', 'Qui tient la comptabilité'),
    { id: 'current_systems', domain: 'systems', label: 'Outils actuels', value: 'Odoo', epistemic_status: 'declared', source: 'booking_form', statement: 'Formulaire de rendez-vous' },
    missing('odoo_scope', 'systems', 'Périmètre d’Odoo'),
    { id: 'shopify', domain: 'systems', label: 'Boutique en ligne', value: 'Shopify', epistemic_status: 'hypothetical', source: 'company_website', confidence: 0.8, statement: 'Technologie détectée sur le site (simulé)' },
  ];
}

interface Step {
  focus: DomainKey;
  /** Texte de la question ; `preamble` la précède quand le tour précédent appelle une réaction. */
  question: QuestionCandidate;
  alternatives: QuestionCandidate[];
  suggested_reply: string;
  /** Mises à jour appliquées quand le client répond à cette question. */
  apply: (items: Map<string, UnderstandingItem>, recommendations: Map<string, Recommendation>, answer: string) => void;
  /** Réaction (reformulation ou objection) placée avant la question suivante. */
  reaction?: string;
}

const q = (id: string, text: string, objective: string, targeted: string[], scores: [number, number, number, number, number, number, number]): QuestionCandidate => {
  const [information_gain, importance, impact, uncertainty, dependency, redundancy, fatigue] = scores;
  return { id, text, objective, targeted, information_gain, importance, impact, uncertainty, dependency, redundancy, fatigue };
};

function declared(items: Map<string, UnderstandingItem>, id: string, value: string, answer: string, extra: Partial<UnderstandingItem> = {}): void {
  const item = items.get(id)!;
  items.set(id, { ...item, value, epistemic_status: 'declared', source: 'interview', validation_status: 'unvalidated', statement: answer, ...extra });
}

const STEPS: Step[] = [
  {
    focus: 'company',
    question: q(
      'q_prepared',
      'J’ai préparé notre entretien. D’après le registre, Maison Exemple SAS prépare des produits à base de viande et compte deux établissements. Votre site présente des saucissons et des terrines, avec une boutique en ligne qui semble utiliser Shopify. Vous m’avez dit utiliser Odoo et vouloir couvrir les stocks, la production, la traçabilité et la qualité. Est-ce une bonne représentation de votre activité ?',
      'Confirmer ou corriger d’un coup ce que la préparation a trouvé.',
      ['legal_name', 'activity', 'sites', 'product_families', 'sales_channels', 'shopify'],
      [0.9, 0.9, 0.9, 0.6, 1.2, 0, 0],
    ),
    alternatives: [q('q_activity', 'Pouvez-vous me décrire votre activité ?', 'Question large, sans la préparation.', ['activity'], [0.6, 0.8, 0.7, 0.4, 1, 0.3, 0])],
    suggested_reply:
      'Oui, sauf qu’on a trois sites maintenant : l’atelier de Lyon, un entrepôt à Villeurbanne et une boutique ouverte en septembre. On vend surtout aux restaurants, et aussi en ligne sur Shopify.',
    apply(items, _recs, answer) {
      for (const id of ['legal_name', 'activity', 'product_families', 'shopify']) items.set(id, { ...items.get(id)!, validation_status: 'confirmed' });
      const registry = items.get('sites')!;
      items.set('sites_registry', { ...registry, id: 'sites_registry', label: 'Sites selon le registre', conflicts_with: ['sites'] });
      declared(items, 'sites', '3 : atelier (Lyon), entrepôt (Villeurbanne), boutique', answer, { conflicts_with: ['sites_registry'] });
      declared(items, 'customer_types', 'Restaurants (principalement), particuliers', answer);
      declared(items, 'sales_channels', 'Vente directe aux restaurants, boutique en ligne Shopify', answer);
      items.set('stock_locations', {
        ...items.get('stock_locations')!,
        value: 'Atelier (Lyon), entrepôt (Villeurbanne), boutique',
        epistemic_status: 'inferred_ai',
        source: 'ai_inference',
        confidence: 0.6,
        reason: 'Les trois sites cités peuvent chacun détenir du stock ; à confirmer.',
      });
    },
    reaction: 'Merci. Je note trois sites, alors que le registre en indique deux : nous y reviendrons.',
  },
  {
    focus: 'organization',
    question: q(
      'q_roles',
      'Avant d’entrer dans le détail : qui fait quoi chez vous ? Par exemple, qui dirige, et qui s’occupe de la production, de la qualité, des ventes et de la comptabilité ?',
      'Connaître l’organisation et les responsabilités : elles donnent les rôles, les droits d’accès et la personne à solliciter pour chaque décision.',
      ['decision_maker', 'roles', 'headcount', 'quality_owner'],
      [0.9, 1, 1.1, 1, 1.4, 0, 0],
    ),
    alternatives: [q('q_lots_early', 'Comment suivez-vous aujourd’hui les lots ?', 'Traçabilité.', ['lot_tracking'], [0.9, 1, 1, 1, 1.1, 0, 0])],
    suggested_reply:
      'Je suis le gérant et je m’occupe aussi des ventes. Le chef d’atelier gère la production, et nous avons une responsable qualité. La comptabilité, c’est notre expert-comptable. Nous sommes 12, dont 8 à l’atelier.',
    apply(items, _recs, answer) {
      declared(items, 'decision_maker', 'Gérant (également responsable des ventes)', answer);
      declared(items, 'roles', 'Gérant : direction et ventes · Chef d’atelier : production · Responsable qualité · Expert-comptable externe : comptabilité', answer);
      declared(items, 'headcount', '12 personnes, dont 8 à l’atelier', answer);
      declared(items, 'quality_owner', 'Responsable qualité (salariée)', answer);
      items.set('user_count', { ...items.get('user_count')!, validation_status: 'confirmed' });
      items.set('quality_approver', {
        id: 'quality_approver',
        domain: 'quality',
        label: 'Validation des lots',
        value: 'Responsable qualité',
        epistemic_status: 'inferred_ai',
        source: 'ai_inference',
        confidence: 0.65,
        reason: 'Une responsable qualité est en poste : c’est elle qui devrait valider les contrôles et libérer les lots. À confirmer.',
      });
    },
    reaction: 'Merci, c’est noté.',
  },
  {
    focus: 'traceability',
    question: q(
      'q_lots',
      'Comment suivez-vous aujourd’hui les lots, de la réception de la viande jusqu’à la vente ?',
      'Comprendre la traçabilité actuelle : elle conditionne les stocks et la production.',
      ['lot_tracking', 'traceability_tool', 'stock_system'],
      [0.9, 1, 1, 1, 1.3, 0, 0.05],
    ),
    alternatives: [
      q('q_refs', 'Combien de références produisez-vous ?', 'Dimensionner le catalogue.', ['product_families'], [0.5, 0.6, 0.5, 0.4, 0.9, 0.1, 0]),
      q('q_suppliers', 'Qui sont vos principaux fournisseurs de viande ?', 'Achats : hors du périmètre déclaré.', [], [0.4, 0.4, 0.3, 1, 0.7, 0, 0]),
    ],
    suggested_reply: 'Les numéros de lot sont notés sur papier à la réception, puis recopiés dans un fichier Excel. Odoo ne sert qu’aux ventes et à la facturation.',
    apply(items, recs, answer) {
      declared(items, 'lot_tracking', 'Papier à la réception, recopié dans Excel', answer);
      declared(items, 'traceability_tool', 'Excel', answer);
      declared(items, 'odoo_scope', 'Ventes et facturation', answer);
      items.set('stock_system', {
        ...items.get('stock_system')!,
        value: 'Aucun outil de stock',
        epistemic_status: 'inferred_ai',
        source: 'ai_inference',
        confidence: 0.7,
        reason: 'Odoo ne couvre que les ventes et la facturation, et les lots sont suivis sur papier puis Excel.',
      });
      recs.set('rec_trace', {
        id: 'rec_trace',
        domain: 'traceability',
        title: 'Remplacer le suivi des lots sur Excel ?',
        current: 'Papier + Excel',
        recommendation: ['replace'],
        rationale: 'Vous voulez gérer la traçabilité dans AYUMIA. Un suivi sur papier recopié dans Excel ne relie pas automatiquement les lots reçus, les produits fabriqués et les ventes.',
        evidence_ids: ['lot_tracking', 'traceability_tool'],
        confidence: 0.82,
      });
    },
  },
  {
    focus: 'systems',
    question: q(
      'q_boundary',
      'Vous voulez gérer les stocks et les lots dans AYUMIA. Pour les ventes et la facturation, préférez-vous les faire passer dans AYUMIA ou les garder dans Odoo ?',
      'Fixer la frontière entre AYUMIA et Odoo : elle décide des intégrations et de la migration.',
      ['odoo_scope', 'accounting_system', 'order_entry'],
      [0.8, 1, 1.2, 0.8, 1.4, 0, 0.05],
    ),
    alternatives: [q('q_qc', 'Quels contrôles qualité faites-vous ?', 'Qualité.', ['quality_controls'], [0.7, 0.9, 0.8, 1, 0.9, 0, 0])],
    suggested_reply: 'On voudrait garder Odoo pour la facturation et la compta, c’est ce qu’utilise notre expert-comptable.',
    apply(items, recs, answer) {
      declared(items, 'accounting_system', 'Odoo', answer);
      declared(items, 'accounting_owner', 'Expert-comptable externe, sur Odoo', answer);
      recs.set('rec_accounting', {
        id: 'rec_accounting',
        domain: 'accounting',
        title: 'Conserver Odoo pour la comptabilité',
        current: 'Odoo (facturation, comptabilité)',
        recommendation: ['keep', 'integrate'],
        rationale: 'La comptabilité fonctionne et votre expert-comptable l’utilise : aucun gain de remplacement n’est établi. AYUMIA lui transmettrait les ventes et les mouvements valorisés.',
        evidence_ids: ['accounting_system', 'accounting_owner'],
        confidence: 0.85,
      });
      recs.set('rec_sales', {
        id: 'rec_sales',
        domain: 'sales',
        title: 'Ventes : rester dans Odoo ?',
        current: 'Odoo Ventes',
        recommendation: ['review'],
        likely: 'replace',
        rationale: 'Si AYUMIA gère les stocks et les lots, saisir les commandes dans Odoo imposerait de synchroniser en permanence les réservations et les lots expédiés.',
        evidence_ids: ['odoo_scope', 'lot_tracking'],
        confidence: 0.7,
      });
    },
    reaction:
      'C’est cohérent pour la comptabilité. Pour les ventes en revanche, si AYUMIA gère les stocks et les lots, garder la saisie des commandes dans Odoo obligerait à synchroniser en permanence les réservations et les lots expédiés.',
  },
  {
    focus: 'sales',
    question: q(
      'q_orders',
      'Qui saisit aujourd’hui les commandes des restaurants, et comment vous parviennent-elles ?',
      'Évaluer ce que coûterait de garder les ventes dans Odoo.',
      ['order_entry'],
      [0.7, 0.9, 1, 1, 1.1, 0, 0.1],
    ),
    alternatives: [],
    suggested_reply: 'C’est moi qui les saisis dans Odoo, à partir des e-mails et des appels. Les commandes Shopify arrivent toutes seules dans Odoo.',
    apply(items, recs, answer) {
      declared(items, 'order_entry', 'Saisie manuelle dans Odoo (e-mails, téléphone) ; Shopify synchronisé avec Odoo', answer);
      const sales = recs.get('rec_sales');
      if (sales !== undefined) recs.set('rec_sales', { ...sales, evidence_ids: [...sales.evidence_ids, 'order_entry'], confidence: 0.74 });
      recs.set('rec_shopify', {
        id: 'rec_shopify',
        domain: 'systems',
        title: 'Conserver Shopify',
        current: 'Shopify',
        recommendation: ['keep', 'integrate'],
        rationale: 'Shopify est votre canal en ligne et il fonctionne. Ses commandes arriveraient dans AYUMIA au lieu d’Odoo.',
        evidence_ids: ['shopify', 'order_entry'],
        confidence: 0.8,
      });
    },
  },
  {
    focus: 'quality',
    question: q(
      'q_quality',
      'Quels contrôles qualité faites-vous, et où les enregistrez-vous (températures, plan HACCP…) ?',
      'Couvrir la qualité, que vous voulez gérer dans AYUMIA.',
      ['quality_controls', 'haccp_plan'],
      [0.8, 0.9, 0.9, 1, 1, 0, 0.1],
    ),
    alternatives: [],
    suggested_reply: 'On relève les températures des chambres froides deux fois par jour sur une feuille, et le plan HACCP est dans un classeur.',
    apply(items, recs, answer) {
      declared(items, 'quality_controls', 'Températures des chambres froides, 2 fois par jour, sur papier', answer);
      declared(items, 'haccp_plan', 'Classeur papier', answer);
      recs.set('rec_quality', {
        id: 'rec_quality',
        domain: 'quality',
        title: 'Passer les contrôles qualité dans AYUMIA',
        current: 'Papier',
        recommendation: ['replace'],
        rationale: 'Des relevés sur papier ne déclenchent aucune alerte et ne sont pas reliés aux lots concernés.',
        evidence_ids: ['quality_controls', 'haccp_plan'],
        confidence: 0.78,
      });
    },
  },
  {
    focus: 'organization',
    question: q(
      'q_sites',
      'Le registre indique deux établissements et vous m’en citez trois. La boutique est-elle un établissement pas encore déclaré, ou appartient-elle à une autre société ?',
      'Lever la contradiction sur les sites : ils structurent les stocks, les lots et les droits d’accès.',
      ['sites', 'sites_registry'],
      [0.6, 1, 1, 1, 1.2, 0, 0.1],
    ),
    alternatives: [],
    suggested_reply: 'La boutique a ouvert en septembre, son établissement n’est pas encore enregistré, mais c’est bien la même société.',
    apply(items, _recs, answer) {
      const sites = items.get('sites')!;
      items.set('sites', { ...sites, conflicts_with: [], validation_status: 'confirmed', statement: answer });
      const registry = items.get('sites_registry');
      if (registry !== undefined) items.set('sites_registry', { ...registry, conflicts_with: [], validation_status: 'superseded', reason: 'Établissement ouvert en septembre, pas encore enregistré.' });
    },
  },
];

const CLOSING =
  'J’ai maintenant assez d’éléments pour préparer une proposition de configuration. Restent ouverts : les recettes et les ordres de fabrication, la validation des lots par la responsable qualité, et votre choix pour les ventes. Voulez-vous voir la synthèse ?';

export interface DemoEngineOptions {
  now?: () => Date;
  latency_ms?: number;
  /** Appelé après chaque changement d'état : le back-office suit l'entretien. */
  onChange?: (state: InterviewState) => void;
}

export function createDemoInterviewEngine(options: DemoEngineOptions = {}): InterviewEngine {
  const now = options.now ?? (() => new Date());
  const latency = options.latency_ms ?? 0;
  const items = new Map<string, UnderstandingItem>();
  const recs = new Map<string, Recommendation>();
  const messages: ChatMessage[] = [];
  let step = 0;
  let status: InterviewState['status'] = 'in_progress';
  let consultant = false;
  let initialOverall = 0;
  let counter = 0;
  const telemetry = { provider: 'simulé', model: 'scénario de démonstration', calls: 0, input_tokens: 0, output_tokens: 0, latency_ms: 0 };

  const wait = (): Promise<void> => (latency > 0 ? new Promise((resolve) => setTimeout(resolve, latency)) : Promise.resolve());
  const say = (role: ChatMessage['role'], text: string, question_id?: string): void => {
    counter += 1;
    messages.push({ id: `m${counter}`, role, text, at: now().toISOString(), ...(question_id === undefined ? {} : { question_id }) });
  };

  function snapshot(): InterviewState {
    const list = [...items.values()];
    const current = STEPS[step];
    const state: InterviewState = {
      status,
      company: items.get('legal_name')?.value ?? '',
      messages: [...messages],
      items: list,
      recommendations: [...recs.values()],
      scope: SCOPE,
      readiness: computeReadiness(PROFILE, list, SCOPE),
      initial_overall: initialOverall,
      consultant_present: consultant,
      telemetry: { ...telemetry },
      simulated: true,
    };
    if (status === 'in_progress' && current !== undefined) {
      state.current_question = { candidate: current.question, candidates: [current.question, ...current.alternatives] };
      state.focus = current.focus;
      state.suggested_reply = current.suggested_reply;
    }
    return state;
  }

  function ask(prefix?: string): void {
    const next = STEPS[step];
    if (next === undefined) {
      status = 'ready_for_review';
      say('ai', prefix === undefined ? CLOSING : `${prefix} ${CLOSING}`);
      return;
    }
    say('ai', prefix === undefined ? next.question.text : `${prefix} ${next.question.text}`, next.question.id);
  }

  function clearConflicts(id: string): void {
    for (const other of items.values()) {
      if (other.conflicts_with?.includes(id)) items.set(other.id, { ...other, conflicts_with: other.conflicts_with.filter((entry) => entry !== id), validation_status: 'superseded' });
    }
  }

  const engine: InterviewEngine = {
    async start() {
      if (messages.length === 0) {
        for (const item of initialItems()) items.set(item.id, item);
        initialOverall = computeReadiness(PROFILE, [...items.values()], SCOPE).overall;
        ask();
      }
      return snapshot();
    },
    async answer(text) {
      say('customer', text);
      await wait();
      const current = STEPS[step];
      if (current !== undefined) {
        current.apply(items, recs, text);
        telemetry.calls += 3;
        step += 1;
        ask(current.reaction);
      }
      return snapshot();
    },
    async defer(kind) {
      const current = STEPS[step];
      say('customer', kind === 'dont_know' ? 'Je ne sais pas.' : 'À confirmer plus tard.');
      await wait();
      if (current !== undefined) {
        for (const id of current.question.targeted) {
          const item = items.get(id);
          if (item !== undefined && item.value === undefined) items.set(id, { ...item, deferred: true });
        }
        step += 1;
        ask(kind === 'dont_know' ? 'Pas de souci, je le note comme point ouvert.' : 'D’accord, nous y reviendrons.');
      }
      return snapshot();
    },
    async correct(itemId, value) {
      const item = items.get(itemId);
      if (item !== undefined) {
        clearConflicts(itemId);
        items.set(itemId, { ...item, value, epistemic_status: 'declared', source: 'customer_correction', validation_status: 'confirmed', statement: value, conflicts_with: [], deferred: false });
        say('system', `Correction enregistrée : ${item.label} → ${value}`);
      }
      return snapshot();
    },
    async confirm(itemId) {
      const item = items.get(itemId);
      if (item !== undefined) {
        clearConflicts(itemId);
        items.set(itemId, { ...item, validation_status: 'confirmed', conflicts_with: [] });
      }
      return snapshot();
    },
    async reject(itemId) {
      const item = items.get(itemId);
      if (item !== undefined) {
        items.set(itemId, { ...item, validation_status: 'rejected' });
        say('system', `Information écartée : ${item.label}`);
      }
      return snapshot();
    },
    async decide(id, decision) {
      const rec = recs.get(id);
      if (rec !== undefined) recs.set(id, { ...rec, decision });
      return snapshot();
    },
    async inviteConsultant() {
      if (!consultant) {
        consultant = true;
        say('system', 'Un consultant AYUMIA a rejoint l’entretien. Il peut intervenir à tout moment.');
        say('consultant', 'Bonjour, je suis là en observateur. Continuez avec AYUMIA, j’interviendrai si besoin.');
      }
      return snapshot();
    },
    async review() {
      status = 'review';
      return snapshot();
    },
  };
  const notify = options.onChange;
  if (notify === undefined) return engine;
  // Chaque action publie l'état rendu à l'écran, sans autre effet.
  const wrapped = {} as InterviewEngine;
  for (const key of Object.keys(engine) as Array<keyof InterviewEngine>) {
    const method = engine[key] as (...args: unknown[]) => Promise<InterviewState>;
    (wrapped as unknown as Record<string, unknown>)[key] = async (...args: unknown[]) => {
      const state = await method(...args);
      notify(state);
      return state;
    };
  }
  return wrapped;
}
